import { z } from 'zod';
import type { QueryClient } from '@tanstack/react-query';
import { purchaseOrderQuery } from '@/api/queries';
import { upsertPurchaseOrder } from '@/api/endpoints';
import type { PurchaseOrderStatus } from '@/api/types';
import type { ToolConfig } from './utils/create-tool';
import { handlePoError } from './utils/po-error-handler';

const SUBMITTABLE_STATUSES: Set<PurchaseOrderStatus> = new Set([
  'open_draft',
  'submitted',
  'amended',
]);

const TERMINAL_STATUSES: Set<PurchaseOrderStatus> = new Set([
  'confirmed',
  'partially_confirmed',
  'rejected',
  'rejected_by_fulfillment',
  'cancelled',
  'closed_received',
]);

const inputSchema = z.object({
  purchase_order_id: z.string(),
});

async function execute(params: z.infer<typeof inputSchema>, queryClient: QueryClient) {
  try {
    // Fetch current PO state
    const po = await queryClient.fetchQuery(purchaseOrderQuery(params.purchase_order_id));

    // Validate status is submittable
    if (TERMINAL_STATUSES.has(po.status)) {
      return {
        error: `Cannot submit — PO ${po.purchaseOrderNumber} is in "${po.status}" status, which is terminal.`,
      };
    }

    if (!SUBMITTABLE_STATUSES.has(po.status)) {
      return {
        error: `Cannot submit — PO ${po.purchaseOrderNumber} is in "${po.status}" status.`,
      };
    }

    // Validate PO has items
    if (!po.purchaseOrderItems.length) {
      return {
        error: `Cannot submit — PO ${po.purchaseOrderNumber} has no line items.`,
      };
    }

    // Submit
    const result = await upsertPurchaseOrder({
      id: po.id,
      brand_id: po.brandId,
      status: 'submitted',
      submit: true,
      changed_by_party: 'retailer',
    });

    const updated = result.data.attributes;

    // Invalidate caches
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] }),
      queryClient.invalidateQueries({ queryKey: ['purchase-order', params.purchase_order_id] }),
    ]);

    return {
      success: true,
      po_number: updated.purchaseOrderNumber,
      status: updated.status,
      brand_name: updated.brandName,
      action_required_by: updated.actionRequiredBy,
      message: `${updated.purchaseOrderNumber} has been submitted to ${updated.brandName}. The brand will review and respond.`,
    };
  } catch (error) {
    return handlePoError(error);
  }
}

export const submitPurchaseOrderTool: ToolConfig<typeof inputSchema> = {
  name: 'submit_purchase_order',
  description:
    'Submits a draft purchase order to the brand for review. Only use this after the operator has explicitly confirmed they want to submit. The PO must be in open_draft, submitted, or amended status.',
  inputSchema,
  execute,
};
